import { usePostHog } from 'posthog-js/react';
import { FC, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { ImagesApp } from '../../../constants/images';
import { useCartContext } from '../../../hooks/contexts/cart.context';
import { useEventContext } from '../../../hooks/contexts/event.context';
import { useI18nContext } from '../../../hooks/contexts/i18n.context';
import { useMembershipContext } from '../../../hooks/contexts/membership.context';
import { useModalContext } from '../../../hooks/contexts/modal.context';
import { ListView, useUiContext } from '../../../hooks/contexts/ui.context';
import useRules from '../../../hooks/useRules';
import { Ticket } from '../../../types/ticket';
import formatNumericValue from '../../../utils/formatNumericValue';
import DateTimeSessionsTags from '../../DateTimeSessionsTags';
import BackIcon from '../../SvgCustom/svg/BackIcon';
import Minus from '../../SvgCustom/svg/Minus';
import Plus from '../../SvgCustom/svg/Plus';
import { Cupom, Partner, Promotional, ReleasedByCode } from '../../Tags';
import FaceEnabled from '../../Tags/FaceEnabled';
import OGlobo from '../../Tags/OGlobo';
import * as Style from './styles.acordeon';

type AcordeonProps = { opened: boolean; ticket: Ticket; idRef: number };

const Acordeon: FC<AcordeonProps> = ({ opened, ticket, idRef }) => {
  const posthog = usePostHog();
  const theme = useTheme();
  const [expanded, setExpanded] = useState<boolean>(opened);
  const [idDateCarousel, setIdDateCarousel] = useState<number>();
  const [clickedTicketId, setClickedTicketId] = useState(0);
  const [heights, setHeights] = useState<Record<number, number>>({});
  const descriptionRefs = useRef<Record<number, HTMLSpanElement | null>>({});
  const [show, setShow] = useState<boolean>(false);
  const [backButtonColor, setBackButtonColor] = useState<string>(theme.colors.text.secondary);

  const { translate } = useI18nContext();

  const {
    checkQuantityIfAddedInCart, getTotalTicketsByGroup,
    cart, addTicketToCart, removeTicketFromCart
  } = useCartContext();

  const {
    startImage, tickets, event,
    currencyFormat, showQuantityInStock,
    isFootball,
    ticketGroupSelected, selectTicketGroup,
    signatureOGloboApplied,
    selectedIdDateCarousel
  } = useEventContext();

  const {
    showModal, setContentModal,
    defineTitleModal, toggleModal
  } = useModalContext();

  const {
    applySelectionTicketRules, checkAnyMembershipEnabledInTheGroup,
    checksIfAllTicketsAreSoldOut, ticketStatus, sessionStatus
  } = useRules();

  const {
    handleChangeListView,
    listView
  } = useUiContext();

  const { membershipApplied } = useMembershipContext();

  const handleToggle = () => setExpanded(!expanded);

  const totalTickets = getTotalTicketsByGroup({ groupId : ticket.id });

  const anyMembershipEnabledInTheGroup = checkAnyMembershipEnabledInTheGroup({ ticket });

  const allTicketsClosed = checksIfAllTicketsAreSoldOut({ ticket });

  const openAccordion = !ticket.finished && !allTicketsClosed;

  const handleMakeModal = ({ title, textContent }: { title: string, textContent: string, index: number }) => {
    setContentModal(
      <Style.ViewMoreTextModal>
        {textContent}
      </Style.ViewMoreTextModal>
    );
    defineTitleModal(title);
    toggleModal({ open : !showModal });
  };

  useEffect(() => {
    ticket.type.forEach((_, index) => {
      if (descriptionRefs && descriptionRefs.current && descriptionRefs.current[index]) {
        setHeights((prev) => ({
          ...prev,
          [index] : descriptionRefs.current[index]!.scrollHeight,
        }));
      }
    });
  }, [ticket.type]);

  useEffect(() => {
    if (listView === 'tickets' && idDateCarousel && idDateCarousel !== selectedIdDateCarousel) {
      selectTicketGroup(null);
      handleChangeListView(ListView.TICKETS);
    }
  }, [selectedIdDateCarousel]);

  useEffect(() => {
    setShow(true);
  }, [expanded]);

  useEffect(() => {
    setExpanded(opened);
  }, [opened]);

  useEffect(() => {
    setIdDateCarousel(selectedIdDateCarousel);
  }, []);
  const hasABackButton = ticketGroupSelected !== null;

  const totalItemsInStock = ticket.type.reduce((acc, type) => acc + type.quantityInStock, 0);

  return (
    <Style.Acordeon $show={show} id={`acordeon-container_${idRef}`}>
      <Style.Header
        $hasABackButton={hasABackButton}
        $opened={false}
        id={`acordeon-header_${idRef}`}
        onMouseEnter={() => setBackButtonColor(theme.colors.accent.primary)}
        onMouseLeave={() => setBackButtonColor(theme.colors.text.secondary)}
        onClick={() => {
          if (hasABackButton) {
            selectTicketGroup(null);
            handleChangeListView(ListView.TICKETS_GROUPS);
          } else {
            handleToggle();
          }
        }}
      >
        {hasABackButton && (
          <Style.BackButton
            id={`acordeon-back-button_${idRef}`}
          >
            <img src={BackIcon({ colorTheme : backButtonColor })} alt="seta para trás" />
          </Style.BackButton>

        )}
        <Style.Title $enabledSale={openAccordion} id={`acordeon-title_${idRef}`}>
          {ticket.name}
        </Style.Title>
        {hasABackButton || (
          <Style.ContentActions id={`acordeon-actions_${idRef}`}>

            {showQuantityInStock && (
              <span id={`acordeon-totalItems-in-stock_${idRef}`}>
                {`${totalItemsInStock - totalTickets} ${translate(['INFORMATION', 'AVAILABLE_TICKETS'])}`}
              </span>
            )}

            {totalTickets > 0 && (
              <span id={`acordeon-total-tickets_${idRef}`}>
                {`${totalTickets} ${totalTickets > 1 ? (
                  translate(['INFORMATION', 'SELECTED_TICKETS'])
                ) : (
                  translate(['INFORMATION', 'SELECTED_TICKET'])
                )}`}
              </span>
            )}

            {openAccordion && (
              <Style.ArrowAction onClick={handleToggle} id={`acordeon-arrow-action_${idRef}`}>
                <img
                  src={ImagesApp.svg[expanded ? 'arrowUp' : 'arrowDown']}
                  alt=""
                  id={`acordeon-arrow-icon_${idRef}`} />
              </Style.ArrowAction>
            )}

            {!openAccordion && (
              <Style.ShortlyText id={`acordeon-shortly-text-sales-ended_${idRef}`}>
                {translate(['INFORMATION', allTicketsClosed ? 'SOLD_OFF' : 'SALES_ENDED']).toUpperCase()}
              </Style.ShortlyText>
            )}

            {openAccordion && !anyMembershipEnabledInTheGroup && (
              <Style.ShortlyText id={`acordeon-shortly-text-shortly_${idRef}`}>
                {translate(['INFORMATION', 'SHORTLY']).toUpperCase()}
              </Style.ShortlyText>
            )}

          </Style.ContentActions>
        )}
      </Style.Header>

      {ticket.type.map((type, index) => {
        const propsSelection = {
          groupId : ticket.id,
          tickets,
          membershipApplied,
          cart,
          event,
          ticketId : type.id
        };
        let captureEvent = 'purchase:ticket_selection_ticket_selection';
        if (ticket.isPassport) {
          captureEvent = 'purchase:ticket_selection_combo_select';
        }
        if (ticket.name === 'COMBO') {
          captureEvent = 'purchase:ticket_selection_combo_view';
        }
        const propsQuantity = { groupId : ticket.id, ticketId : type.id };
        const status = sessionStatus({ ticket : type });
        sessionStatus({ ticket : type });

        const isPromotional = type.category && type.category.name === 'Promocional';
        const isTransferable = type.isTransferableGuestType;

        const quantityInCart = checkQuantityIfAddedInCart(propsQuantity);
        const { disableAddToCart, errorMessage } = applySelectionTicketRules({ ...propsSelection, clickedTicketId });

        return (
          <Style.Content key={type.id} $opened={openAccordion && expanded} id={`acordeon-content_${idRef}_${index}`}>
            <Style.RowFullWidth
              $handleTags={true}
              id={`acordeon-row-full-width-title-section_${idRef}_${index}`}
            >
              <Style.WrapperTitle
                $hasStartImage={Boolean(startImage)}
                id={`acordeon-wrapper-title-section_${idRef}_${index}`}
                $handleTags={true}
              >
                <Style.TitleSection $enabled={status.canSell}
                  id={`acordeon-title-section_${idRef}_${index}`}>
                  {type.name}
                </Style.TitleSection>
                <Style.WrapperTags
                  id={`acordeon-wrapper-tags_${idRef}_${index}`} $hasStartImage={Boolean(startImage)}
                >
                  {(type.hidden && !isFootball) && <ReleasedByCode />}
                  {(type.hidden && isFootball) && <Partner />}
                  {isPromotional && <Promotional />}
                  {ticket.faceRecognition && <FaceEnabled />}
                  {type.tagType && !signatureOGloboApplied && <Cupom coupon={type.tagType.coupon} />}
                  {type.tagType && signatureOGloboApplied && <OGlobo />}
                </Style.WrapperTags>
              </Style.WrapperTitle>
              {showQuantityInStock && (
                <Style.QtdeInStock id={`acordeon-totalItems-in-stock_${idRef}`}>
                  {`${type.quantityInStock - quantityInCart} ${translate([
                    'INFORMATION',
                    'AVAILABLE_TICKETS_DETAIL'
                  ])}`}
                </Style.QtdeInStock>
              )}
            </Style.RowFullWidth>
            <Style.WrapperExpanded>
              {type.description && (
                <Style.Description
                  ref={(el) => (descriptionRefs.current[index] = el)}
                  $enabled={status.canSell}
                  $height={heights[index] || 0}
                  id={`acordeon-description_${idRef}_${index}`}
                >
                  {type.description}
                </Style.Description>
              )}
              {type.description && type.description.length > 100 && (
                <Style.ToggleExpanded onClick={() => handleMakeModal({
                  title : type.name,
                  textContent : type.description,
                  index,
                })} id={`acordeon-toggle-expanded_${idRef}_${index}`}>
                  <span id={`acordeon-toggle-expanded-text_${idRef}_${index}`}>
                    {translate(['INFORMATION', 'VIEW_MORE'])}
                  </span>
                </Style.ToggleExpanded>
              )}
            </Style.WrapperExpanded>
            {type.tagType && (
              <Style.Wrapper id={`acordeon-wrapper-taxed-text_${idRef}_${index}`}>
                <Style.RowFullWidth id={`acordeon-row-full-width-taxed-text_${idRef}_${index}`}>
                  <Style.TaxedText id={`acordeon-taxed-text_${idRef}_${index}`}>
                    {formatNumericValue({ value : type.originalPrice, discount : 0, currencyFormat })}
                  </Style.TaxedText>
                </Style.RowFullWidth>
              </Style.Wrapper>
            )}
            {!isTransferable && (
              <Style.RowFullWidth id={`acordeon-row-full-width-non-transferable-container_${idRef}_${index}`}>
                <Style.Wrapper id={`acordeon-wrapper-non-transferable-container_${idRef}_${index}`}>
                  <Style.TransferableText id={`acordeon-non-transferable-text_${idRef}_${index}`}>
                    {translate(['INFORMATION', 'NON_TRANSFERABLE_TICKET'])}
                  </Style.TransferableText>
                </Style.Wrapper>
              </Style.RowFullWidth>
            )}
            {ticket.isPassport && (
              <DateTimeSessionsTags dates={type.dates} />
            )}
            <Style.RowFullWidth id={`acordeon-row-full-width-tax-container_${idRef}_${index}`}>
              <Style.Wrapper id={`acordeon-wrapper-tax-container_${idRef}_${index}`}>
                <Style.ValueText $enabled={status.canSell} id={`acordeon-value-tax_${idRef}_${index}`}>
                  {type.price > 0
                    ? formatNumericValue({ value : type.price, discount : 0, currencyFormat })
                    : translate(['INFORMATION', 'FREE'])}
                </Style.ValueText>
                {(type.price > 0 && type.tax > 0) && (
                  <Style.ValueInfoText id={`acordeon-value-info-text_${idRef}_${index}`}>
                    {translate(['STATUS_BAR', 'TEXT_5'])}
                  </Style.ValueInfoText>)}
              </Style.Wrapper>
              {status.canSell ? (
                <Style.ContainerActions id={`acordeon-container-actions_${idRef}_${index}`}>
                  <Style.Actions id={`acordeon-actions_${idRef}_${index}`}>
                    {quantityInCart > 0 && (
                      <Style.Selector
                        $disabled={false}
                        id={`acordeon-selector-remove_${idRef}_${index}`}
                        onClick={() => removeTicketFromCart({
                          groupId : ticket.id,
                          ticketId : type.id,
                          restrictions : type.restrictions
                        })}
                      >
                        <img src={Minus({ colorTheme : theme.colors.accent.primary })} alt="" />
                      </Style.Selector>
                    )}
                    <Style.Quantity id={`acordeon-quantity_${idRef}_${index}`}>{quantityInCart}</Style.Quantity>
                    <Style.Selector
                      $disabled={disableAddToCart}
                      id={`acordeon-selector-add_${idRef}_${index}`}
                      onClick={() => {
                        if (!disableAddToCart) {
                          setClickedTicketId(type.id);
                          addTicketToCart({
                            groupId : ticket.id,
                            ticketId : type.id,
                            restrictions : type.restrictions
                          });
                          posthog?.capture(captureEvent);
                        }
                      }}
                    >
                      <img src={Plus({ colorTheme : theme.colors.accent.primary })} alt="" />
                    </Style.Selector>
                  </Style.Actions>
                  {errorMessage && (
                    <Style.ErrorMessage id={`acordeon-error-text_${idRef}_${index}`}>
                      {errorMessage}
                    </Style.ErrorMessage>
                  )}
                </Style.ContainerActions>
              ) : (
                <Style.ShortlyText id={`acordeon-shortly-text-sold-off_${idRef}_${index}`}>
                  {ticketStatus({ ticket : type }).toUpperCase()}
                </Style.ShortlyText>
              )}
            </Style.RowFullWidth>
          </Style.Content>
        );
      })}
    </Style.Acordeon>
  );
};

export default Acordeon;
